import { Link, useOutletContext } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import StitchPageHeader from '@/components/StitchPageHeader';
import type { ProjectOutletContext } from '@/types/projectOutlet';
import { useBuildInfo } from '@/hooks/useBuildInfo';
import {
  getFrontendBuildConstants,
  isVersionInInclusiveRange,
  shortSha,
} from '@/utils/semverRange';

const blocks: { title: string; body: string }[] = [
  {
    title: 'Navigation',
    body:
      'The sidebar holds Dashboard, Requirements, Verifications, Use cases, Traceability (with Coverage, Hierarchy, DSM and Matrix tabs), Baselines and Reports & exports. Project settings at the bottom has tabs for General, Members & reviewers, Catalog, Storage, Notifications and Import. Switch projects, or open Groups and New project, from the Projects menu in the header. Instance administrators find Administration in the user menu. On a narrow screen, open the sidebar with the menu button.',
  },
  {
    title: 'Search',
    body:
      'The header search filters the Requirements table and Verifications list on their respective pages. Open a project first, then go to the list you want to filter.',
  },
  {
    title: 'Traceability',
    body:
      'The Graph view has two subtabs. Coverage shows requirement ↔ verification links (suspect links use coral styling). Hierarchy shows parent ↔ child links between requirements (solid blue) and between verifications (dashed green); a Requirements / Verifications / Both filter restricts the view and is remembered in the URL (?kind=). Double-click a node to open the requirement or verification view page.',
  },
  {
    title: 'Use cases',
    body:
      'A use case lists preconditions, numbered main steps, alternatives (A3) and exceptions (E4) branching from a step, and postconditions. Edit opens a draft shared by the project and saved as you type: Enter adds the next step, Alt+↑/↓ or drag moves it, the ⋯ menu adds a branch. Numbers follow the order, and branches stay attached to their step. Publish version creates the next version for reviewers to mark reviewed and approve.',
  },
  {
    title: 'Creating records',
    body:
      'Use Create requirement or Create verification from the Create button in the header (its menu also has Import). New requirements need at least one verification method configured for the project. Import Excel/CSV (requirements, verifications, or a matrix-links file of codes) or ReqIF (a .reqif file, or a .reqifz archive that also brings its attached files) from Project settings › Import (or Create → Import).',
  },
  {
    title: 'Administration, reports and import',
    body:
      'Administrators open Administration from the user menu: Users, System logs, Log analytics (events per day, top actions, top users) and, on self-hosted installations, Backup. Reports includes Excel (requirements, verifications, matrix grid, matrix links), PDF, and ReqIF downloads served by the API, including a ReqIFZ archive with the attachment files. Import ReqIF or ReqIFZ from Project settings › Import.',
  },
];

export default function HelpPage() {
  const { projectId, basePath } = useOutletContext<ProjectOutletContext>();
  const pid = projectId;
  const { dashboard } = useDashboard();
  const ui = getFrontendBuildConstants();
  const { build, error: buildError } = useBuildInfo();
  const uiSha = shortSha(ui.gitSha);
  const apiSha = shortSha(build?.backend_git_sha);

  const projectName =
    dashboard?.projects?.find((p) => p.id === pid)?.name ?? 'Project';

  const compatible =
    build != null &&
    isVersionInInclusiveRange(
      ui.version,
      build.frontend_compatibility.min_version,
      build.frontend_compatibility.max_version,
    ) &&
    isVersionInInclusiveRange(
      build.backend_version,
      ui.requiresBackendMin,
      ui.requiresBackendMax,
    );

  return (
    <div>
      <StitchPageHeader
        projectName={projectName}
        section="Help"
        title="Help & reference"
        subtitle="Quick orientation for the RVM-style workspace."
      />

      <div className="space-y-4 max-w-3xl">
        <div
          className="rounded-xl border border-stitch-border bg-stitch-surface p-5 shadow-stitch"
          data-testid="help-build-info"
        >
          <h3 className="text-sm font-bold text-stitch-accent uppercase tracking-wide mb-2">
            Versions
          </h3>
          <dl className="text-sm text-stitch-muted space-y-1.5">
            <div className="flex gap-2">
              <dt className="font-semibold text-stitch-fg w-36 shrink-0">UI</dt>
              <dd>
                {ui.version}
                {uiSha ? <span className="ml-2 font-mono text-xs">({uiSha})</span> : null}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-semibold text-stitch-fg w-36 shrink-0">API</dt>
              <dd>
                {buildError
                  ? 'unavailable'
                  : build
                    ? build.backend_version
                    : '…'}
                {apiSha ? <span className="ml-2 font-mono text-xs">({apiSha})</span> : null}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-semibold text-stitch-fg w-36 shrink-0">Deployment</dt>
              <dd>
                {buildError
                  ? 'unavailable'
                  : build
                    ? build.deployment_mode
                    : '…'}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-semibold text-stitch-fg w-36 shrink-0">Compatible</dt>
              <dd data-testid="help-compatible">
                {buildError ? 'unknown' : build == null ? '…' : compatible ? 'yes' : 'no'}
              </dd>
            </div>
          </dl>
        </div>

        {blocks.map((b) => (
          <div
            key={b.title}
            className="rounded-xl border border-stitch-border bg-stitch-surface p-5 shadow-stitch"
          >
            <h3 className="text-sm font-bold text-stitch-accent uppercase tracking-wide mb-2">
              {b.title}
            </h3>
            <p className="text-sm text-stitch-muted leading-relaxed">{b.body}</p>
          </div>
        ))}

        <div className="rounded-xl border border-stitch-border bg-stitch-elevated p-5">
          <h3 className="text-sm font-bold text-stitch-fg uppercase tracking-wide mb-3">Shortcuts</h3>
          <ul className="text-sm text-stitch-muted space-y-2">
            <li>
              <Link to={`${basePath}/dashboard`} className="text-stitch-accent font-semibold hover:underline">
                Dashboard
              </Link>{' '}
              — project KPIs
            </li>
            <li>
              <Link to={`${basePath}/settings/import`} className="text-stitch-accent font-semibold hover:underline">
                Import
              </Link>{' '}
              — Excel / CSV / ReqIF
            </li>
            <li>
              <Link to={`${basePath}/reports`} className="text-stitch-accent font-semibold hover:underline">
                Reports
              </Link>{' '}
              — coverage gaps
            </li>
            <li>
              <Link to={`${basePath}/settings`} className="text-stitch-accent font-semibold hover:underline">
                Project settings
              </Link>{' '}
              — members, catalog, storage
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
